import React from 'react';
import ConfirmationModal from '@enerex/unified-ui-react/ConfirmationModal';
import Div from 'components/Div';
import Text from 'components/Text';
import CustomerIcon from '@enerex/unified-ui-react/icons/CustomerIcon';
import { Link } from 'react-router-dom';
import { buildCompanyLogoUrl } from 'utils/logo';
import CompanyHierarchyTree from '../CompanyHierarchyTree';

interface HierarchyNode {
    id: string;
    name: string;
    domain?: string;
    levelLabel?: string;
    isCurrent: boolean;
    children?: HierarchyNode[];
}

interface ViewHierarchyModalProps {
    show: boolean;
    loading: boolean;
    hierarchyTree: HierarchyNode | null;
    onClose: () => void;
    logo_error: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
}

const renderHierarchyNode = (node: HierarchyNode, depth: number, logo_error: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void): React.ReactNode => {
    if (depth > 4) {
        return null;
    }

    const hasChildren = node.children && node.children.length > 0;
    
    // Progressive blue-gray shades: white -> light blue-gray -> darker blue-gray
    const backgroundColors = [
        '#ffffff',      // Level 0: white
        '#f5f7fa',      // Level 1: very light blue-gray
        '#e8edf3',      // Level 2: light blue-gray
        '#dae4ed',      // Level 3: medium light blue-gray
        '#cbd8e6',      // Level 4: medium blue-gray
    ];
    
    // Use light gold background for current company, otherwise use depth-based colors
    const backgroundColor = node.isCurrent ? '#fff9e6' : backgroundColors[Math.min(depth, backgroundColors.length - 1)];
    const padding = depth === 0 ? 16 : 12;

    return (
        <Div
            key={`${node.name}-${depth}-${node.domain}`}
            style={{
                backgroundColor: backgroundColor,
                borderRadius: 8,
                border: node.isCurrent ? '2px solid #f0c674' : '1px solid #d0d0d0',
                padding: padding,
                marginBottom: hasChildren ? 0 : 8,
                marginTop: depth > 0 ? 8 : 0,
            }}
        >
            {/* Company info */}
            <Div className="d-flex align-items-center mb-2">
                {node.domain && (
                    <img
                        src={buildCompanyLogoUrl(node.domain)}
                        alt={node.name}
                        style={{
                            width: 32,
                            height: 32,
                            objectFit: 'contain',
                            marginRight: 10,
                        }}
                        onError={logo_error}
                    />
                )}
                <Div className="d-flex flex-column">
                    {node.levelLabel && (
                        <Text
                            variant="plain"
                            className="text-muted mb-1"
                            style={{ fontSize: 11 }}
                        >
                            {node.levelLabel}
                        </Text>
                    )}
                    {node.id ? (
                        <Link
                            to={`/companies/${node.id}`}
                            style={{
                                textDecoration: 'none',
                                color: 'inherit',
                                fontSize: depth === 0 ? '16px' : '14px',
                                fontWeight: '600'
                            }}
                        >
                            {node.name}
                        </Link>
                    ) : (
                        <Text variant="plain" className="fw-semibold mb-0" style={{ fontSize: depth === 0 ? '16px' : '14px' }}>
                            {node.name}
                        </Text>
                    )}
                    {node.domain && (
                        <Text
                            variant="link"
                            className="mb-0"
                            url={`http://${node.domain}`}
                            style={{ fontSize: '12px' }}
                        >
                            {node.domain}
                        </Text>
                    )}
                </Div>
            </Div>

            {/* Children nested inside */}
            {hasChildren && (
                <Div style={{ marginTop: 12 }}>
                    {node.children!.map((child) =>
                        renderHierarchyNode(child, depth + 1, logo_error)
                    )}
                </Div>
            )}
        </Div>
    );
};

const ViewHierarchyModal: React.FC<ViewHierarchyModalProps> = ({
    show,
    loading,
    hierarchyTree,
    onClose,
    logo_error,
}) => {
    return (
        <ConfirmationModal
            dialogClassName="modal-md"
            centered
            show={show}
            onHide={onClose}
            size="lg"
            title="Company Hierarchy"
            confirmButtonLabel="Close"
            onConfirm={onClose}
        >
                {loading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : hierarchyTree ? (
                    <Div className="d-flex flex-column" style={{ maxHeight: 500, overflowY: 'auto', padding: '10px' }}>
                        {/* {renderHierarchyNode(hierarchyTree, 0, logo_error)} */}
                        <CompanyHierarchyTree data={hierarchyTree} />
                    </Div>
                ) : (
                    <Div className="text-center text-muted py-4">
                        No hierarchy data available for this company.
                    </Div>
                )}
        </ConfirmationModal>
    );
};

export default ViewHierarchyModal;

